import React, { useState } from 'react';
import './TopWidgetEdit.css';
import './FancyFooterEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import NavigationCategoriesManagement from '../WidgetEditComponents/NavigationCategoriesManagement';
import SocialLinksManagement from '../WidgetEditComponents/SocialLinksManagement';
import LegalLinksManagement from '../WidgetEditComponents/LegalLinksManagement';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';
import LayoutSelector from '../WidgetEditComponents/LayoutSelector';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function FancyFooterEdit({ widget, onUpdate, colors, pageData }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);

    // Image Upload States
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // Dark/Light Mode Presets
    const applyDarkMode = () => {
        console.log('🌙 Applying Dark Mode preset...');
        
        const darkModeSettings = {
            backgroundColor: '#1a1a1a',
            textColor: '#ffffff',
            headingColor: '#ffffff',
            linkColor: '#cccccc',
            linkHoverColor: '#ffffff',
            accentColor: '#5C80BC',
            bottomBarBackground: '#111111',
            bottomBarTextColor: '#888888',
            borderTopColor: '#333333',
            logoInvert: true,
            socialIconStyle: 'brand-colors'
        };
        
        // Create combined update object
        const updatedData = { ...editData, ...darkModeSettings };
        
        // Direct update to avoid multiple Firebase calls
        onUpdate(updatedData);
        
        console.log('✅ Dark Mode applied:', darkModeSettings);
    };

    const applyLightMode = () => {
        console.log('☀️ Applying Light Mode preset...');
        
        const lightModeSettings = {
            backgroundColor: '#ffffff',
            textColor: '#1f2937',
            headingColor: '#111827',
            linkColor: '#4b5563',
            linkHoverColor: '#1f2937',
            accentColor: '#3b82f6',
            bottomBarBackground: '#f9fafb',
            bottomBarTextColor: '#6b7280',
            borderTopColor: '#e5e7eb',
            logoInvert: false,
            socialIconStyle: 'outline-dark'
        };
        
        // Create combined update object
        const updatedData = { ...editData, ...lightModeSettings };
        
        // Direct update to avoid multiple Firebase calls
        onUpdate(updatedData);
        
        console.log('✅ Light Mode applied:', lightModeSettings);
    };

    // Get Social Media Icon (FontAwesome Classes) - Kopiert vom Widget
    const getSocialIcon = (platform) => {
        const platformLower = platform.toLowerCase();
        
        const iconMap = {
            'facebook': 'fab fa-facebook-f',
            'twitter': 'fab fa-twitter',
            'x': 'fab fa-x-twitter',
            'instagram': 'fab fa-instagram',
            'linkedin': 'fab fa-linkedin-in',
            'youtube': 'fab fa-youtube',
            'tiktok': 'fab fa-tiktok',
            'pinterest': 'fab fa-pinterest-p',
            'snapchat': 'fab fa-snapchat-ghost',
            'discord': 'fab fa-discord',
            'telegram': 'fab fa-telegram-plane',
            'whatsapp': 'fab fa-whatsapp',
            'github': 'fab fa-github',
            'reddit': 'fab fa-reddit-alien',
            'tumblr': 'fab fa-tumblr',
            'twitch': 'fab fa-twitch',
            'spotify': 'fab fa-spotify',
            'soundcloud': 'fab fa-soundcloud',
            'vimeo': 'fab fa-vimeo-v',
            'behance': 'fab fa-behance',
            'dribbble': 'fab fa-dribbble',
            'medium': 'fab fa-medium-m',
            'slack': 'fab fa-slack',
            'skype': 'fab fa-skype',
            'zoom': 'fas fa-video',
            'email': 'fas fa-envelope',
            'website': 'fas fa-globe',
            'blog': 'fas fa-blog'
        };
        
        if (iconMap[platformLower]) {
            return iconMap[platformLower];
        }
        
        for (const [key, icon] of Object.entries(iconMap)) {
            if (platformLower.includes(key) || key.includes(platformLower)) {
                return icon;
            }
        }
        
        return 'fas fa-link';
    };

    // Get Platform Color - Kopiert vom Widget
    const getPlatformColor = (platform) => {
        const platformLower = platform.toLowerCase();
        
        const colorMap = {
            'facebook': '#1877F2',
            'twitter': '#1DA1F2',
            'x': '#000000',
            'instagram': '#E4405F',
            'linkedin': '#0A66C2',
            'youtube': '#FF0000',
            'tiktok': '#000000',
            'pinterest': '#BD081C',
            'snapchat': '#FFFC00',
            'discord': '#5865F2',
            'telegram': '#26A5E4',
            'whatsapp': '#25D366',
            'github': '#181717',
            'reddit': '#FF4500',
            'tumblr': '#00CF35',
            'twitch': '#9146FF',
            'spotify': '#1DB954',
            'soundcloud': '#FF3300',
            'vimeo': '#1AB7EA',
            'behance': '#1769FF',
            'dribbble': '#EA4C89',
            'medium': '#000000',
            'slack': '#4A154B',
            'skype': '#00AFF0',
            'zoom': '#2D8CFF',
            'email': '#EA4335',
            'website': '#4285F4',
            'blog': '#FF6B35'
        };
        
        return colorMap[platformLower] || '#5C80BC';
    };

    // Layout Change Handler
    const handleLayoutChange = (layoutId, layoutConfig) => {
        const fakeEvent = {
            target: {
                name: 'layoutType',
                value: layoutId,
                type: 'text'
            }
        };
        handleInputChange(fakeEvent);
    };

    // Categories Management
    const addCategory = () => {
        const currentCategories = editData.categories || [];
        const newCategory = {
            id: `cat${Date.now()}`,
            title: 'New Category',
            links: [
                { text: 'New Link', url: '#' }
            ]
        };
        const updatedCategories = [...currentCategories, newCategory];
        handleInputChange({ target: { name: 'categories', value: updatedCategories } });
    };

    const removeCategory = (categoryIndex) => {
        const currentCategories = editData.categories || [];
        const updatedCategories = currentCategories.filter((_, index) => index !== categoryIndex);
        handleInputChange({ target: { name: 'categories', value: updatedCategories } });
    };

    const updateCategoryTitle = (categoryIndex, newTitle) => {
        const currentCategories = editData.categories || [];
        const updatedCategories = [...currentCategories];
        updatedCategories[categoryIndex] = { ...updatedCategories[categoryIndex], title: newTitle };
        handleInputChange({ target: { name: 'categories', value: updatedCategories } });
    };

    const addLinkToCategory = (categoryIndex) => {
        const currentCategories = editData.categories || [];
        const updatedCategories = [...currentCategories];
        const newLink = { text: 'New Link', url: '#' };
        updatedCategories[categoryIndex].links = [...(updatedCategories[categoryIndex].links || []), newLink];
        handleInputChange({ target: { name: 'categories', value: updatedCategories } });
    };

    const removeLinkFromCategory = (categoryIndex, linkIndex) => {
        const currentCategories = editData.categories || [];
        const updatedCategories = [...currentCategories];
        updatedCategories[categoryIndex].links = updatedCategories[categoryIndex].links.filter((_, index) => index !== linkIndex);
        handleInputChange({ target: { name: 'categories', value: updatedCategories } });
    };

    const updateCategoryLink = (categoryIndex, linkIndex, field, value) => {
        const currentCategories = editData.categories || [];
        const updatedCategories = [...currentCategories];
        updatedCategories[categoryIndex].links[linkIndex] = {
            ...updatedCategories[categoryIndex].links[linkIndex],
            [field]: value
        };
        handleInputChange({ target: { name: 'categories', value: updatedCategories } });
    };

    // Get available widgets for selection
    const getAvailableWidgets = () => {
        if (!pageData || !pageData.widgets) return [];
        
        return pageData.widgets.map((widget, index) => ({
            id: widget.id,
            name: widget.name || `${widget.type} ${index + 1}`,
            type: widget.type
        }));
    };

    // Social Links Management
    const addSocialLink = () => {
        const currentSocial = editData.socialLinks || [];
        const newSocial = {
            platform: 'new-platform',
            url: 'https://',
            icon: '/img/icons/social.svg'
        };
        const updatedSocial = [...currentSocial, newSocial];
        handleInputChange({ target: { name: 'socialLinks', value: updatedSocial } });
    };

    const removeSocialLink = (index) => {
        const currentSocial = editData.socialLinks || [];
        const updatedSocial = currentSocial.filter((_, i) => i !== index);
        handleInputChange({ target: { name: 'socialLinks', value: updatedSocial } });
    };

    const updateSocialLink = (index, field, value) => {
        const currentSocial = editData.socialLinks || [];
        const updatedSocial = [...currentSocial];
        updatedSocial[index] = { ...updatedSocial[index], [field]: value };
        handleInputChange({ target: { name: 'socialLinks', value: updatedSocial } });
    };

    // Legal Links Management
    const addLegalLink = () => {
        const currentLegal = editData.legalLinks || [];
        const newLegal = { text: 'New Legal Link', url: '#' };
        const updatedLegal = [...currentLegal, newLegal];
        handleInputChange({ target: { name: 'legalLinks', value: updatedLegal } });
    };

    const removeLegalLink = (index) => {
        const currentLegal = editData.legalLinks || [];
        const updatedLegal = currentLegal.filter((_, i) => i !== index);
        handleInputChange({ target: { name: 'legalLinks', value: updatedLegal } });
    };

    const updateLegalLink = (index, field, value) => {
        const currentLegal = editData.legalLinks || [];
        const updatedLegal = [...currentLegal];
        updatedLegal[index] = { ...updatedLegal[index], [field]: value };
        handleInputChange({ target: { name: 'legalLinks', value: updatedLegal } });
    };

    // Text Configuration
    const textConfig = [
        {
            fieldName: 'companyName',
            label: 'Company Name',
            type: 'input',
            placeholder: 'Your Company',
            fontField: 'headingFont',
            fontType: 'big',
            effectField: 'headingEffect'
        },
        {
            fieldName: 'slogan',
            label: 'Company Slogan',
            type: 'textarea',
            placeholder: 'Your inspiring slogan here...',
            rows: 2,
            fontField: 'bodyFont',
            fontType: 'small',
            effectField: 'sloganEffect'
        },
        {
            fieldName: 'address',
            label: 'Address',
            type: 'textarea',
            placeholder: '123 Business Street\nYour City, State 12345',
            rows: 3,
            fontField: 'bodyFont',
            fontType: 'small'
        },
        {
            fieldName: 'phone',
            label: 'Phone Number',
            type: 'input',
            placeholder: '+1 (555) 123-4567',
            fontField: 'bodyFont',
            fontType: 'small'
        },
        {
            fieldName: 'email',
            label: 'Email Address',
            type: 'input',
            placeholder: 'contact@yourcompany.com',
            fontField: 'bodyFont',
            fontType: 'small'
        },
        {
            fieldName: 'copyrightText',
            label: 'Copyright Text',
            type: 'input',
            placeholder: '© 2025 Your Company. All rights reserved.',
            fontField: 'bodyFont',
            fontType: 'small'
        }
    ];

    // Color Configuration
    const colorConfig = [
        [
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#1a1a1a',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'textColor',
                label: 'Text Color',
                defaultValue: '#ffffff',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'headingColor',
                label: 'Headings',
                defaultValue: '#ffffff',
                icon: '/img/text-fat.svg'
            }
        ],
        [
            {
                fieldName: 'linkColor',
                label: 'Links',
                defaultValue: '#cccccc',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'linkHoverColor',
                label: 'Link Hover',
                defaultValue: '#ffffff',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'accentColor',
                label: 'Accent Color',
                defaultValue: '#5C80BC',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'bottomBarBackground',
                label: 'Bottom Bar BG',
                defaultValue: '#111111',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'bottomBarTextColor',
                label: 'Bottom Bar Text',
                defaultValue: '#888888',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'borderTopColor',
                label: 'Border Color',
                defaultValue: '#333333',
                icon: '/img/border.svg'
            }
        ]
    ];

    // Font Configuration
    const fontConfig = [
        {
            label: 'Headings',
            sizeField: 'headingSize',
            weightField: 'headingWeight',
            defaultSize: '18',
            defaultWeight: '600'
        },
        {
            label: 'Body Text',
            sizeField: 'bodySize',
            weightField: 'bodyWeight',
            defaultSize: '14',
            defaultWeight: '400'
        },
        {
            label: 'Slogan',
            sizeField: 'sloganSize',
            weightField: 'sloganWeight',
            defaultSize: '16',
            defaultWeight: '400'
        }
    ];

    // 🚀 REVOLUTIONARY LAYOUT SETTINGS - Custom Properties für Fancy Footer Widget
    const customLayoutProperties = [
        {
            section: 'Logo Settings',
            icon: 'fa-image',
            properties: [
                {
                    id: 'logoHeight',
                    label: 'Logo Height',
                    min: 30,
                    max: 100,
                    step: 5,
                    unit: 'px',
                    icon: '🏢',
                    description: 'Height of the footer logo'
                }
            ]
        },
        {
            section: 'Column Settings',
            icon: 'fa-columns',
            properties: [
                {
                    id: 'columnSpacing',
                    label: 'Column Spacing',
                    min: 20,
                    max: 80,
                    step: 5,
                    unit: 'px',
                    icon: '📋',
                    description: 'Space between footer columns'
                },
                {
                    id: 'sectionSpacing',
                    label: 'Section Spacing',
                    min: 20,
                    max: 60,
                    step: 5,
                    unit: 'px',
                    icon: '📄',
                    description: 'Space between footer sections'
                }
            ]
        },
        {
            section: 'Social Icons',
            icon: 'fa-share-alt',
            properties: [
                {
                    id: 'socialIconSize',
                    label: 'Icon Size',
                    min: 16,
                    max: 32,
                    step: 2,
                    unit: 'px',
                    icon: '🔗',
                    description: 'Size of social media icons'
                },
                {
                    id: 'socialIconSpacing',
                    label: 'Icon Spacing',
                    min: 8,
                    max: 24,
                    step: 2,
                    unit: 'px',
                    icon: '📱',
                    description: 'Space between social icons'
                }
            ]
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - ALLE Footer Settings zusammengefasst
    const advancedConfig = [
        // Dimensions Group
        {
            fieldName: 'logoWidth',
            label: 'Logo Width',
            type: 'slider',
            defaultValue: 120,
            min: 60,
            max: 300,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#5C80BC',
            description: 'Width of the footer logo'
        },
        {
            fieldName: 'logoHeight',
            label: 'Logo Height',
            type: 'slider',
            defaultValue: 60,
            min: 30,
            max: 150,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#5C80BC',
            description: 'Height of the footer logo'
        },

        // Spacing Group
        {
            fieldName: 'paddingTop',
            label: 'Padding Top',
            type: 'number',
            defaultValue: 60,
            min: 20,
            max: 120,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'paddingBottom',
            label: 'Padding Bottom',
            type: 'number',
            defaultValue: 40,
            min: 20,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg',
            iconStyle: { rotate: '180deg' }
        },
        {
            fieldName: 'paddingX',
            label: 'Padding Sides',
            type: 'number',
            defaultValue: 40,
            min: 20,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'columnGap',
            label: 'Column Gap',
            type: 'number',
            defaultValue: 40,
            min: 20,
            max: 80,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'rowGap',
            label: 'Row Gap',
            type: 'number',
            defaultValue: 30,
            min: 15,
            max: 60,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'bottomBarPadding',
            label: 'Bottom Bar Padding',
            type: 'number',
            defaultValue: 20,
            min: 10,
            max: 40,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },

        // Behavior Group
        {
            fieldName: 'showLogo',
            label: 'Show Logo',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'logoInvert',
            label: 'Logo Invert (White Logo)',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showContactInfo',
            label: 'Contact Information',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showNavigation',
            label: 'Navigation Categories',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showSocialLinks',
            label: 'Social Links',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showBottomBar',
            label: 'Bottom Bar',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showLegalLinks',
            label: 'Legal Links',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'borderTop',
            label: 'Top Border',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },

        // Display Group
        {
            fieldName: 'borderTopWidth',
            label: 'Border Width',
            type: 'number',
            defaultValue: 1,
            min: 1,
            max: 5,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* Dark/Light Mode Quick Setup */}
                <div className="widget-edit-section" style={{ marginBottom: '20px' }}>
                    <h4 className="section-title" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px'
                    }}>
                        <i className="fas fa-palette" style={{ fontSize: '14px', color: '#5C80BC' }}></i>
                        Theme Presets
                    </h4>
                    
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginBottom: '12px'
                    }}>
                        <button
                            onClick={applyDarkMode}
                            className="theme-preset-btn dark-mode-btn"
                            title="Apply dark mode: Dark backgrounds, white text, inverted logo"
                        >
                            <div className="theme-btn-icon">
                                <i className="fas fa-moon"></i>
                            </div>
                            <div className="theme-btn-content">
                                <span className="theme-btn-title">Dark Mode</span>
                                <span className="theme-btn-desc">Dark footer theme</span>
                            </div>
                        </button>

                        <button
                            onClick={applyLightMode}
                            className="theme-preset-btn light-mode-btn"
                            title="Apply light mode: Light backgrounds, dark text, original logo"
                        >
                            <div className="theme-btn-icon">
                                <i className="fas fa-sun"></i>
                            </div>
                            <div className="theme-btn-content">
                                <span className="theme-btn-title">Light Mode</span>
                                <span className="theme-btn-desc">Light footer theme</span>
                            </div>
                        </button>
                    </div>
                    
                    <div style={{
                        padding: '8px 12px',
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                        borderRadius: '6px',
                        border: '1px solid #bae6fd'
                    }}>
                        <p style={{
                            fontSize: '11px',
                            color: '#0369a1',
                            margin: '0',
                            textAlign: 'center',
                            fontWeight: '500',
                            lineHeight: '1.4'
                        }}>
                            <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                            One-click presets that automatically set all colors, logo invert, and social icon styles
                        </p>
                    </div>
                </div>
                
                {/* Layout Selector */}
                <LayoutSelector
                    widgetType="fancyFooter"
                    selectedLayout={editData.layoutType || 'corporate-layout'}
                    onLayoutChange={handleLayoutChange}
                />
                
                {/* 🚀 LOGO IMAGE CONTAINER - Revolutionary System 2025 */}
                <ImageContainer
                    label="Logo"
                    fieldName="logoImage"
                    currentImage={editData.logoImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingLogo}
                    setIsUploading={setIsUploadingLogo}
                    showToggle={true}
                    toggleLabel="Show Logo"
                    toggleFieldName="showLogo"
                    toggleChecked={editData.showLogo || false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'size',
                            label: 'Logo Width',
                            fieldName: 'logoWidth',
                            defaultValue: 120,
                            min: 50,
                            max: 300,
                            step: 5,
                            unit: 'px',
                            description: 'Adjust the logo width'
                        },
                        {
                            type: 'size',
                            label: 'Logo Height',
                            fieldName: 'logoHeight',
                            defaultValue: 60,
                            min: 30,
                            max: 150,
                            step: 5,
                            unit: 'px',
                            description: 'Adjust the logo height'
                        },
                        {
                            type: 'toggle',
                            label: 'Logo Invert (White Logo)',
                            fieldName: 'logoInvert'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 📝 REVOLUTIONARY TEXT SETTINGS - Widget Text Refactoring 2025 */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                />

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard + Custom Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* 🔧 ADVANCED SETTINGS - New Component replacing Logo & Display Settings */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* 🧭 NAVIGATION CATEGORIES MANAGEMENT - New Toggle Section */}
                {editData.showNavigation && (
                    <NavigationCategoriesManagement 
                        categories={editData.categories || []}
                        addCategory={addCategory}
                        removeCategory={removeCategory}
                        updateCategoryTitle={updateCategoryTitle}
                        addLinkToCategory={addLinkToCategory}
                        removeLinkFromCategory={removeLinkFromCategory}
                        updateCategoryLink={updateCategoryLink}
                        editData={editData}
                    />
                )}

                {/* 📱 SOCIAL LINKS MANAGEMENT - New Toggle Section */}
                {editData.showSocialLinks && (
                    <SocialLinksManagement 
                        socialLinks={editData.socialLinks || []}
                        addSocialLink={addSocialLink}
                        removeSocialLink={removeSocialLink}
                        updateSocialLink={updateSocialLink}
                        editData={editData}
                    />
                )}

                {/* ⚖️ LEGAL LINKS MANAGEMENT - New Toggle Section */}
                {editData.showLegalLinks && (
                    <LegalLinksManagement 
                        legalLinks={editData.legalLinks || []}
                        addLegalLink={addLegalLink}
                        removeLegalLink={removeLegalLink}
                        updateLegalLink={updateLegalLink}
                        editData={editData}
                    />
                )}

                {/* More Settings (Colors & Fonts only) */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Advanced Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
            
        </div>
            
    );
}

export default FancyFooterEdit;
